import React, { FC, useState, useEffect } from 'react';
import {
    Icon,
    SelectionMode,
    DetailsListLayoutMode,
    OverflowSet,
    IOverflowSetItemProps,
    CommandBarButton,
    IconButton,
    IIconProps,
} from '@fluentui/react';
import { useTranslation } from 'react-i18next';
import { DetailsList, TextField, Toggle, LayoutContent} from '@eta/components';

import { EditableItem } from './editableItem';
import { Code } from '../models/code';

import './CodeManagement.scss';


const validateIcon: IIconProps = { iconName: 'CheckMark' };
const cancelIcon: IIconProps = { iconName: 'Clear' };

const onRenderItemStyles = {
    root: { padding: '10px' },
    menuIcon: { fontSize: '16px' },
};
const onRenderOverflowButtonStyles = {
    root: { padding: '10px' },
    menuIcon: { fontSize: '16px' },
};
const onRenderItem = (item: IOverflowSetItemProps): JSX.Element => {
    return (
        <CommandBarButton
            role="menuitem"
            aria-label={item.name}
            styles={onRenderItemStyles}
            iconProps={{ iconName: item.icon }}
            onClick={item.onClick}
        />
    );
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const onRenderOverflowButton = (overflowItems?: any[]): JSX.Element => {
    return (
        <CommandBarButton
            role="menuitem"
            title="More items"
            styles={onRenderOverflowButtonStyles}
            menuIconProps={{ iconName: 'MoreVertical' }}
            menuProps={{ items: overflowItems ?? [] }}
        />
    );
};

export const CodeManagement: FC = () => {
    const [items, setItems] = useState<EditableItem<Code>[]>([]);
    const {t} = useTranslation('common');
    useEffect(() => {
        const codes: Code[] = [
            {
                id: 'Code1',
                isMinLevel: false,
                levelName: 'GPC Level 1 code - segment',
                levelNameAR: 'رمز المستوى 1 من GPC - الجزء',
                description:
                    'this is a free online calculator which counts the number of characters or letters in a text, useful!',
                descriptionAr:
                    'هذه آلة حاسبة مجانية على الإنترنت تحسب عدد الأحرف أو الأحرف في النص ، مفيدة!',
            },
            {
                id: 'Code2',
                isMinLevel: false,
                levelName: 'GPC Level 2 code - segment',
                levelNameAR: 'رمز المستوى 2 من GPC - الجزء',
                description:
                    'this is a free online calculator which counts the number of characters or letters in a text, useful!',
                descriptionAr:
                    'هذه آلة حاسبة مجانية على الإنترنت تحسب عدد الأحرف أو الأحرف في النص ، مفيدة!',
            },
            {
                id: 'Code3',
                isMinLevel: true,
                levelName: 'GPC Level 3 code - segment',
                levelNameAR: 'رمز المستوى 3 من GPC - الجزء',
                description:
                    'this is a free online calculator which counts the number of characters or letters in a text, useful!',
                descriptionAr:
                    'هذه آلة حاسبة مجانية على الإنترنت تحسب عدد الأحرف أو الأحرف في النص ، مفيدة!',
            },
            {
                id: 'Code4',
                isMinLevel: false,
                levelName: 'GPC Level 4 code - segment',
                levelNameAR: 'رمز المستوى 4 من GPC - الجزء',
                description:
                    'this is a free online calculator which counts the number of characters or letters in a text, useful!',
                descriptionAr:
                    'هذه آلة حاسبة مجانية على الإنترنت تحسب عدد الأحرف أو الأحرف في النص ، مفيدة!',
            },
            {
                id: 'Code5',
                isMinLevel: false,
                levelName: 'GPC Level 5 code - segment',
                levelNameAR: 'رمز المستوى 5 من GPC - الجزء',
                description:
                    'this is a free online calculator which counts the number of characters or letters in a text, useful!',
                descriptionAr:
                    'هذه آلة حاسبة مجانية على الإنترنت تحسب عدد الأحرف أو الأحرف في النص ، مفيدة!',
            },
        ];
        setItems(
            codes.map((c) => {
                return { item: c, editedItem: c };
            })
        );
    }, []);

    const changeItem = (id: string, newItem: EditableItem<Code>) => {
        const originalItem = items.filter((i) => i.item.id === id)[0];
        const index = items.indexOf(originalItem);
        const newItems = items.slice();
        newItems[index] = { ...originalItem, ...newItem };
        setItems(newItems);
    };
    const onChangeLock = (item: EditableItem<Code>, value: boolean) => {
        changeItem(item.item.id, { ...item, isLocked: value });
    };
    const onValidateEdition = (item: EditableItem<Code>) => {
        changeItem(item.item.id, {
            ...item,
            item: { ...item.editedItem },
            isEdited: false,
        });
    };
    const onCancelEdition = (item: EditableItem<Code>) => {
        changeItem(item.item.id, { ...item, isEdited: false });
    };
    const onEdit = (item: EditableItem<Code>) => {
        changeItem(item.item.id, { ...item, isEdited: true });
    };
    const onDelete = (item: EditableItem<Code>) => {
        setItems(items.filter((i) => i.item.id !== item.item.id));
    };
    const columns = [
        {
            key: 'levelName',
            name: 'Level Name',
            fieldName: 'levelName',
            ariaLabel: ' Press to sort on TaxPayer Name',
            valueAccesor: (v: EditableItem<Code>) => v.item,
            minWidth: 100,
            maxWidth: 250,
            data: 'string',
            isResizable: true,
            isSortedDescending: true,
            isRowHeader: true,
            onRender: (data: EditableItem<Code>) => {
                return (
                    <div>
                        <Icon
                            iconName="BackToWindow"
                            title="Select min choice level"
                            className={
                                data.item.isMinLevel
                                    ? 'minLevelIcon-selected'
                                    : 'minLevelIcon'
                            }
                        />
                        <span>{data.item.levelName}</span>
                    </div>
                );
            },
        },
        {
            key: 'levelNameAR',
            name: 'Level Name [AR]',
            fieldName: 'levelNameAR',
            valueAccesor: (v: EditableItem<Code>) => v.item,
            minWidth: 110,
            maxWidth: 250,
            isRowHeader: true,
            isResizable: true,
            isSorted: false,
            isSortedDescending: false,
            sortAscendingAriaLabel: 'Sorted A to Z',
            sortDescendingAriaLabel: 'Sorted Z to A',
            data: 'string',
            isPadded: true,
            
            onRender: (data: EditableItem<Code>) => {
                return (
                    <div className="horizontal">
                        <span>{data.item.levelNameAR}</span>
                    </div>
                );
            },
        },
        {
            key: 'description',
            name: 'Description',
            fieldName: 'description',
            valueAccesor: (v: EditableItem<Code>) => v.item,
            minWidth: 200,
            maxWidth: 350,
            isRowHeader: true,
            isResizable: true,
            isSorted: false,
            isSortedDescending: false,
            sortAscendingAriaLabel: 'Sorted A to Z',
            sortDescendingAriaLabel: 'Sorted Z to A',
            data: 'string',
            isPadded: true,
            
            onRender: (data: EditableItem<Code>) => {
                return data.isEdited ? (
                    <TextField
                        multiline
                        readOnly={data.isLocked}
                        value={data.editedItem.description}
                        onChange={(_, value) =>
                            changeItem(data.item.id, {
                                ...data,
                                editedItem: {
                                    ...data.editedItem,
                                    description: value ?? '',
                                },
                            })
                        }
                    />
                ) : (
                    <span>{data.item.description}</span>
                );
            },
        },
        {
            key: 'descriptionAr',
            name: 'Description [AR]',
            fieldName: 'descriptionAr',
            valueAccesor: (v: EditableItem<Code>) => v.item,
            minWidth: 200,
            maxWidth: 350,
            isRowHeader: true,
            isResizable: true,
            isSorted: false,
            isSortedDescending: false,
            sortAscendingAriaLabel: 'Sorted A to Z',
            sortDescendingAriaLabel: 'Sorted Z to A',
            data: 'string',
            isPadded: true,
            
            onRender: (data: EditableItem<Code>) => {
                return data.isEdited ? (
                    <TextField
                        multiline
                        arabic
                        readOnly={data.isLocked}
                        value={data.editedItem.descriptionAr}
                        onChange={(_, value) =>
                            changeItem(data.item.id, {
                                ...data,
                                editedItem: {
                                    ...data.editedItem,
                                    descriptionAr: value ?? '',
                                },
                            })
                        }
                    />
                ) : (
                    <span dir="rtl">{data.item.descriptionAr}</span>
                );
            },
        },
        {
            key: 'action',
            name: '',
            minWidth: 70,
            maxWidth: 90,
            isResizable: true,
            data: 'string',
            isPadded: true,
            
            onRender: (data: EditableItem<Code>) => {
                return data.isEdited ? (
                    <div className="horizontal">
                        <Toggle
                            checked={data.isLocked}
                            onChange={(_, value) =>
                                onChangeLock(data, value ?? false)
                            }
                        />
                        <IconButton
                            iconProps={validateIcon}
                            onClick={() => onValidateEdition(data)}
                        />
                        <IconButton
                            iconProps={cancelIcon}
                            onClick={() => onCancelEdition(data)}
                        />
                    </div>
                ) : (
                    <div className="horizontal">
                        <Icon iconName={data.isLocked ? 'Lock' : ''} />
                        <OverflowSet
                            role="menubar"
                            vertical
                            overflowItems={[
                                {
                                    key: 'editButton',
                                    iconProps: {
                                        iconName: 'Edit',
                                    },
                                    name: 'Edit',
                                    onClick: () => {
                                        onEdit(data);
                                    },
                                },
                                {
                                    key: 'deleteButton',
                                    iconProps: {
                                        iconName: 'Delete',
                                    },
                                    name: 'Delete',
                                    onClick: () => {
                                        onDelete(data);
                                    },
                                },
                            ]}
                            onRenderOverflowButton={onRenderOverflowButton}
                            onRenderItem={onRenderItem}
                        />
                    </div>
                );
            },
        },
    ];

    return (
        <LayoutContent >
            <DetailsList
                className="grid"
                columns={columns ?? []}
                items={items}
                selectionMode={SelectionMode.none}
                layoutMode={DetailsListLayoutMode.justified}
                noItemsPlaceholder={<span>{t('common:NoRecords')} </span>}
                labels={{resultPerPage: t('common:nbItemPerPage'), totalRecord:t('common:totalRecord')}}
                paginationSetting={{
                    nbItemPerPage: 6,
                    nbPageShown: 3 /* currentPage: 0, totalPage: 20 when managed externaly */,
                }}
            />
        </LayoutContent>
    );
};
